import * as Sentry from '@sentry/react';

import { i18n as I18n } from '@nw/shared/config';

import { resStatuses, statusCodes } from '../constants/APIErrorConstants';

interface ErrorMessages {
  errorViewTitle?: string;
  errorViewDescription?: string;
  connectionLost?: string;
  internetConnection?: string;
}

export const displayApiError = (
  apiError: any,
  showAlert = false,
  errorMessages: ErrorMessages = {
    errorViewTitle: I18n.t('common:common.errorViewTitle'),
    errorViewDescription: I18n.t('common:common.errorViewDescription'),
    connectionLost: I18n.t('common:common.connectionLost'),
    internetConnection: I18n.t('common:common.internetConnection')
  }
) => {
  const {
    errorViewTitle,
    errorViewDescription,
    connectionLost,
    internetConnection
  } = errorMessages;

  let description = '';
  let errorConstant = '';
  let title: string = errorViewTitle as string;
  let errorCode: number = statusCodes.internalServerErrorCode;

  if (apiError !== null && apiError !== undefined) {
    try {
      const parsedMessage: any = JSON.parse(apiError);
      let parsedError: any;

      if (parsedMessage.data === undefined || parsedMessage.data === null) {
        // To handle case when we are directly returning backend  error message
        parsedError = parsedMessage;
      } else {
        // To handle case when we are adding all the requests to backend error message
        parsedError = parsedMessage.data;
      }

      if (parsedError !== undefined && parsedError !== null) {
        if (
          parsedError.message &&
          parsedError.message === resStatuses.requestTimedOut
        ) {
          title = errorViewTitle as string;
          description = errorViewDescription as string;
        }

        if (parsedError.response) {
          try {
            const response = JSON.parse(parsedError.response);
            const { title: errorTitle, description: errorDescription } =
              response;
            if (errorTitle) {
              title = errorTitle;
            }
            if (errorDescription) {
              description = errorDescription;
            } else {
              description = parsedError.response;
            }
          } catch (e) {
            description = parsedError.response;
          }
          errorConstant = parsedError.res_status;
        }
        if (parsedError.http_status_code) {
          errorCode = parsedError.http_status_code;
          errorConstant = parsedError.res_status;
          if (
            parsedError.http_status_code === statusCodes.noInternetErrorCode
          ) {
            title = connectionLost as string;
            description = internetConnection as string;
          }
        }
      }
      if (
        errorCode === 503 &&
        errorConstant === resStatuses.dbMaintenanceError
      ) {
        title = I18n.t('common:common.serverErrorTitle');
        description = I18n.t('common:common.serverErrorDescription');
        errorConstant = resStatuses.internalServerError;
      }
    } catch (e) {
      if (apiError) {
        console.error(apiError);
      }
    }
  }

  if (description === null || description === '') {
    title = errorViewTitle as string;
    description = errorViewDescription as string;
  }
  if (showAlert) {
    alert(description); // eslint-disable-line
  }
  const apiErrorResponse = {
    errorCode,
    title,
    description,
    errorConstant
  };
  return apiErrorResponse;
};

export const getAPIErrorMessage = (apiError: any) => {
  const error = displayApiError(apiError);
  return error.description;
};

export const getErrorConstant = (apiError) =>
  displayApiError(apiError.message).errorConstant;

export const getFormattedAPIErrorDescription = (apiError: any) => {
  const error = displayApiError(apiError);
  return error.description;
};

export const getAPIErrorResponse = (apiError) => {
  try {
    const errorMessage = apiError?.message;

    // Check if the error is related to invalid JSON or unexpected XML
    if (errorMessage?.includes("Unexpected token '<'")) {
      // Return a generic message if parsing XML failed
      return 'An XML response error occurred, but could not be parsed.';
    }
    const errorResponse =
      errorMessage && JSON.parse(errorMessage)?.data?.response;
    return errorResponse ? errorResponse : getAPIErrorMessage(apiError);
  } catch (error) {
    Sentry.captureException(error);
  }

  return getAPIErrorMessage(apiError);
};

export const getAPIErrorEnum = (apiError): string => {
  try {
    const errorMessage = apiError?.message;
    return errorMessage ? JSON.parse(errorMessage).data?.res_status : '';
  } catch (error) {
    Sentry.addBreadcrumb({
      type: 'info',
      category: 'APIError',
      message: `In getAPIErrorEnum`,
      data: {
        errorType: apiError?.message ? typeof apiError.message : 'undefined',
        originalMessage: apiError?.message,
        problem: apiError?.problem,
        errorResponse: apiError?.errorResponse
      }
    });
    Sentry.captureException(error);
  }
  return '';
};

export const getAPIErrorStringifiedResponse = (apiError) => {
  try {
    return JSON.parse(getAPIErrorResponse(apiError));
  } catch (error) {
    Sentry.captureException(error);
  }
};

export const isMaintenanceError = (error: Error): boolean => {
  const { serverUnderMaintenance } = resStatuses;
  const errorConstant = displayApiError(error.message).errorConstant;
  return errorConstant === serverUnderMaintenance ? true : false;
};
